import { saveBlob } from "./download";
import { fmtEUR, fmtDate, labelize, VEHICLE_STATUS, GENERIC_STATUS, deadlineSeverity } from "./format";
import { colorSwatch } from "./vehicleColors";

// Printing a list: what the page shows (its filters and the columns this person
// may see) becomes a plain table on white paper, with title, date and site.
// A cell is text, or {text, swatch} for a vehicle's colour dot.

const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const cellText = (cell) => (cell && typeof cell === "object" ? cell.text : cell) ?? "";

/** Who prints, when and for which site: the line under the title. */
export function printMeta(user, count) {
  const site = user?.active_site_id === "all" ? "Tutte le sedi"
    : (user?.sites || []).find((s) => s.id === user?.active_site_id)?.name;
  const when = new Date().toLocaleString("it-IT", { timeZone: "Europe/Rome", dateStyle: "short", timeStyle: "short" });
  return [`Stampato il ${when}`, user?.name && `da ${user.name}`, site && `Sede: ${site}`, `${count} ${count === 1 ? "riga" : "righe"}`].filter(Boolean).join(" · ");
}

export function printDocument({ title, meta, columns, rows }) {
  const cell = (value) => {
    const text = escapeHtml(cellText(value)) || "—";
    return value && typeof value === "object" && value.swatch
      ? `<span class="dot" style="background:${escapeHtml(value.swatch)}"></span>${text}` : text;
  };
  const head = columns.map((c) => `<th${c.align === "right" ? ' class="num"' : ""}>${escapeHtml(c.label)}</th>`).join("");
  const body = rows.map((row) => `<tr>${row.map((value, i) => `<td${columns[i]?.align === "right" ? ' class="num"' : ""}>${cell(value)}</td>`).join("")}</tr>`).join("");
  return `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>
@page { size: A4 ${columns.length > 6 ? "landscape" : "portrait"}; margin: 12mm; }
* { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
body { font: 10pt/1.35 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; color: #111; margin: 0; }
h1 { font-size: 15pt; margin: 0 0 2mm; } p.meta { color: #555; margin: 0 0 5mm; font-size: 9pt; }
table { width: 100%; border-collapse: collapse; } thead { display: table-header-group; }
th { text-align: left; font-size: 8.5pt; text-transform: uppercase; letter-spacing: .03em; color: #444; border-bottom: 1.5px solid #333; padding: 2mm 2mm; }
td { border-bottom: 0.5px solid #ccc; padding: 1.6mm 2mm; vertical-align: top; } tr { break-inside: avoid; }
.num { text-align: right; font-variant-numeric: tabular-nums; }
.dot { display: inline-block; width: 3mm; height: 3mm; border-radius: 50%; border: 0.4px solid #666; margin-right: 1.5mm; vertical-align: -0.4mm; }
</style></head><body><h1>${escapeHtml(title)}</h1><p class="meta">${escapeHtml(meta)}</p>
<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`;
}

function printFrame(load) {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
  document.body.appendChild(frame);
  const done = () => setTimeout(() => frame.remove(), 1000);
  frame.onload = () => {
    frame.contentWindow.addEventListener("afterprint", done);
    frame.contentWindow.focus();
    frame.contentWindow.print();
    setTimeout(done, 60000);
  };
  load(frame);
  return frame;
}

/** Open the browser's print dialog for a table, without leaving the page. */
export function printTable({ title, user, columns, rows }) {
  const html = printDocument({ title, meta: printMeta(user, rows.length), columns, rows });
  return printFrame((frame) => { frame.srcdoc = html; });
}

/** Print a PDF the server made (the report), as it would be downloaded. */
export function printPdf(blob) {
  const url = URL.createObjectURL(blob);
  const frame = printFrame((f) => { f.src = url; });
  setTimeout(() => URL.revokeObjectURL(url), 120000);
  return frame;
}

/** Same CSV as the server's exports: BOM for Excel, formulas kept as text. */
export function csvText(columns, rows) {
  const field = (value) => {
    let text = String(cellText(value));
    if (/^\s*[=+\-@]/.test(text) || /^[\t\r\n]/.test(text)) text = "'" + text;
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  return "﻿" + [columns.map((c) => field(c.label)), ...rows.map((row) => row.map(field))].map((line) => line.join(",")).join("\r\n") + "\r\n";
}

export function downloadCsv(filename, columns, rows) {
  saveBlob(new Blob([csvText(columns, rows)], { type: "text/csv;charset=utf-8" }), filename);
}

// A cell of the generic pages (CrudPage columns) as text, for printing.
export function printCell(row, col, refData) {
  const v = row[col.key];
  if (col.type === "currency") return fmtEUR(v);
  if (col.type === "date") return fmtDate(v);
  if (col.type === "badge") return v ? ((col.kind === "vehicle" ? VEHICLE_STATUS : GENERIC_STATUS)[v]?.label || labelize(v)) : "—";
  if (col.type === "deadline") return deadlineSeverity(row.expiry_date).label;
  if (col.type === "plate") {
    const color = refData.vehicles?.find((x) => x.id === row.vehicle_id)?.color;
    return { text: [v || "—", color && `(${color})`].filter(Boolean).join(" "), swatch: colorSwatch(color) };
  }
  if (col.type === "labelize") return labelize(v);
  if (col.type === "ref") {
    const list = col.refType === "suppliers" ? refData.suppliers : col.refType === "locations" ? refData.locations : refData.assignees;
    return list.find((x) => x.id === v)?.name || "—";
  }
  return v ?? "—";
}
